import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Firestore,
  collection,
  query,
  where,
  getDoc,
  getDocs,
  collectionData,
  Timestamp,
  orderBy,
  addDoc,
  setDoc,
  doc,
  limit,
  startAfter,
  QueryDocumentSnapshot
} from '@angular/fire/firestore';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface AppLog {
  id: string;
  timestamp: any;
  userId: string;
  displayName: string;
  email: string;
  platform: string;
  device: string;
  ip: string;
  location: string;
  userAgent?: string;
}

export interface PresenceUser {
  presenceId: string;
  userId: string;
  displayName: string;
  email: string;
  lastActive: any;
  platform: string;
  device: string;
  ip: string;
  location: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserAnalysisService {
  private presenceIdKey = 'aluguloo_presence_id';
  private presenceId: string | null = null;
  private heartbeatInterval: any;
  private clientLocation = 'Unknown';

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private auth: Auth,
    private http: HttpClient
  ) {
    this.presenceId = localStorage.getItem(this.presenceIdKey);
    if (!this.presenceId) {
      this.presenceId = 'web_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem(this.presenceIdKey, this.presenceId);
    }
  }

  /**
   * Fetch current location of the user using ipapi.co
   */
  async detectLocation(): Promise<string> {
    try {
      const res = await this.http.get<any>('https://ipapi.co/json/').toPromise();
      if (res && res.city && res.country_name) {
        this.clientLocation = `${res.city}, ${res.country_name}`;
      } else if (res && res.country_name) {
        this.clientLocation = res.country_name;
      }
    } catch (e) {
      console.warn('Geolocation detection failed, defaulting to Unknown:', e);
    }
    return this.clientLocation;
  }

  /**
   * Get client IP using ipify
   */
  async getClientIp(): Promise<string> {
    try {
      const res = await this.http.get<any>('https://api.ipify.org?format=json').toPromise();
      return res?.ip || 'client-direct';
    } catch (e) {
      return 'client-direct';
    }
  }

  /**
   * Resolve the best display name for the current user:
   * 1. Firebase Auth displayName (set for Google/social logins)
   * 2. firstname field from users-list Firestore doc (set during email/phone registration)
   * 3. email address
   * 4. 'Guest Visitor' fallback
   */
  private async getUserDisplayName(uid: string, email: string): Promise<string> {
    // For social logins (Google, Apple) Firebase Auth sets displayName directly
    if (this.auth.currentUser?.displayName) {
      return this.auth.currentUser.displayName;
    }
    // For email/phone registrations, name is stored in users-list Firestore doc
    if (uid && uid !== 'guest') {
      try {
        const userDocRef = doc(this.firestore, 'users-list', uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          const data = userSnap.data() as any;
          const name = data['firstname'] || data['displayname'] || data['displayName'] || data['fullname'];
          if (name) return name;
        }
      } catch (e) {
        console.warn('Could not fetch user displayName from users-list:', e);
      }
    }
    return email || 'Guest Visitor';
  }

  private lastLoggedUid: string | null = null;
  private isTrackingAuth = false;

  async logSessionEntry(): Promise<void> {
    if (this.isTrackingAuth) return;
    this.isTrackingAuth = true;

    onAuthStateChanged(this.auth, async (user) => {
      const currentUid = user?.uid || 'guest';
      if (this.lastLoggedUid === currentUid) return;
      this.lastLoggedUid = currentUid;

      try {
        await this.detectLocation();
        const deviceName = this.getBrowserName();
        const email = user?.email || '';
        const displayName = await this.getUserDisplayName(currentUid, email);
        const ip = await this.getClientIp();

        try {
          const logFn = httpsCallable(this.functions, 'logAppEntry');
          await logFn({
            platform: 'web',
            device: deviceName,
            location: this.clientLocation,
            ip: ip,
            displayName: displayName
          });
        } catch (e) {
          console.warn('Failed to log session entry via Cloud Function, writing directly to Firestore:', e);
          try {
            const logsCol = collection(this.firestore, 'applogs');
            await addDoc(logsCol, {
              timestamp: Timestamp.now(),
              userId: currentUid,
              displayName: displayName,
              email: email,
              platform: 'web',
              device: deviceName,
              ip: ip,
              location: this.clientLocation
            });
          } catch (err) {
            console.error('Direct Firestore logging failed:', err);
          }
        }
      } catch (err) {
        console.error('Error in logSessionEntry:', err);
      }
    });
  }

  /**
   * Start sending heartbeats for online status tracking
   */
  startPresenceTracking(): void {
    if (this.heartbeatInterval) return;

    // Send immediately
    this.sendHeartbeat();

    // Set interval every 60 seconds
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 60000);
  }

  /**
   * Stop tracking presence
   */
  stopPresenceTracking(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private async sendHeartbeat(): Promise<void> {
    const deviceName = this.getBrowserName();
    const uid = this.auth.currentUser?.uid || null;
    const email = this.auth.currentUser?.email || '';
    const displayName = await this.getUserDisplayName(uid || 'guest', email);
    const presenceId = this.presenceId || uid || 'guest_' + Math.random().toString(36).substring(2, 11);
    const ip = await this.getClientIp();

    try {
      const presenceFn = httpsCallable(this.functions, 'updatePresence');
      await presenceFn({
        presenceId: presenceId,
        platform: 'web',
        device: deviceName,
        location: this.clientLocation,
        ip: ip,
        displayName: displayName
      });
    } catch (e) {
      console.warn('Failed to send presence heartbeat via Cloud Function, updating directly in Firestore:', e);
      try {
        const presenceDocRef = doc(this.firestore, 'presence', presenceId);
        await setDoc(presenceDocRef, {
          presenceId,
          userId: uid || 'guest',
          displayName: displayName,
          email: email,
          lastActive: Timestamp.now(),
          platform: 'web',
          device: deviceName,
          ip: ip,
          location: this.clientLocation
        }, { merge: true });
      } catch (err) {
        console.error('Direct Firestore presence update failed:', err);
      }
    }
  }

  getFirestoreInstance() {
    return this.firestore;
  }

  /**
   * Get real-time list of online users (active within the last 2 minutes)
   */
  getOnlineUsers(): Observable<PresenceUser[]> {
    const presenceCol = collection(this.firestore, 'presence');
    // We cannot use complex server-side query with dynamic relative time in simple collectionData
    // without composite indexes, but we can query everything or filter client-side.
    // Since online users count is usually small, filtering client-side is extremely reliable and easy.
    return collectionData(presenceCol, { idField: 'presenceId' }).pipe(
      map((users: any[]) => {
        const nowMs = Date.now();
        const twoMinutesAgo = nowMs - 2 * 60 * 1000;
        return users.filter(user => {
          if (!user.lastActive) return false;
          let activeMs = 0;
          if (user.lastActive instanceof Timestamp) {
            activeMs = user.lastActive.toMillis();
          } else if (user.lastActive.seconds) {
            activeMs = user.lastActive.seconds * 1000;
          } else {
            activeMs = new Date(user.lastActive).getTime();
          }
          return activeMs >= twoMinutesAgo;
        }) as PresenceUser[];
      })
    );
  }

  /**
   * Fetch logs within a specific date range
   */
  async getLogsRange(startDate: Date, endDate: Date): Promise<AppLog[]> {
    const logsCol = collection(this.firestore, 'applogs');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      logsCol,
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp),
      orderBy('timestamp', 'desc')
    );

    try {
      const snap = await getDocs(q);
      return snap.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data
        } as AppLog;
      });
    } catch (e) {
      console.error('Error fetching logs range:', e);
      // Fallback: fetch all and filter in memory if index not yet generated
      try {
        const fallbackQ = query(logsCol, orderBy('timestamp', 'desc'));
        const fallbackSnap = await getDocs(fallbackQ);
        const allLogs = fallbackSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        } as AppLog));
        
        return allLogs.filter(log => {
          if (!log.timestamp) return false;
          const logMs = log.timestamp instanceof Timestamp ? log.timestamp.toMillis() : new Date(log.timestamp).getTime();
          return logMs >= startDate.getTime() && logMs <= endDate.getTime();
        });
      } catch (err) {
        console.error('Fallback logs fetch failed:', err);
        return [];
      }
    }
  }

  /**
   * Fetch a single page of logs using cursor-based Firestore pagination.
   * Returns the logs, the last document (cursor for next page), and hasMore flag.
   */
  async getLogsPage(
    startDate: Date,
    endDate: Date,
    pageSize: number,
    startAfterDoc?: QueryDocumentSnapshot | null
  ): Promise<{ logs: AppLog[]; lastDoc: QueryDocumentSnapshot | null; hasMore: boolean }> {
    const logsCol = collection(this.firestore, 'applogs');
    const startTs = Timestamp.fromDate(startDate);
    const endTs   = Timestamp.fromDate(endDate);

    // Fetch one extra record to determine if a next page exists
    const constraints: any[] = [
      where('timestamp', '>=', startTs),
      where('timestamp', '<=', endTs),
      orderBy('timestamp', 'desc'),
      limit(pageSize + 1)
    ];
    if (startAfterDoc) {
      constraints.splice(3, 0, startAfter(startAfterDoc));
    }

    try {
      const snap = await getDocs(query(logsCol, ...constraints));
      const hasMore = snap.docs.length > pageSize;
      const pageDocs = hasMore ? snap.docs.slice(0, pageSize) : snap.docs;
      const lastDoc  = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

      const logs = pageDocs.map(d => ({ id: d.id, ...d.data() } as AppLog));
      return { logs, lastDoc, hasMore };
    } catch (e) {
      console.error('getLogsPage error:', e);
      return { logs: [], lastDoc: null, hasMore: false };
    }
  }

  /**
   * Count unique users (distinct userId values) within a date range.
   * Returns { total, unique, web, mobile } counts.
   */
  async getStatsForRange(startDate: Date, endDate: Date): Promise<{
    total: number; unique: number; web: number; mobile: number;
  }> {
    const logsCol = collection(this.firestore, 'applogs');
    const startTs = Timestamp.fromDate(startDate);
    const endTs   = Timestamp.fromDate(endDate);

    try {
      const q = query(
        logsCol,
        where('timestamp', '>=', startTs),
        where('timestamp', '<=', endTs),
        orderBy('timestamp', 'desc')
      );
      const snap = await getDocs(q);
      const allLogs = snap.docs.map(d => ({ id: d.id, ...d.data() } as AppLog));

      const total  = allLogs.length;
      const web    = allLogs.filter(l => l.platform?.toLowerCase() === 'web').length;
      const mobile = allLogs.filter(l => l.platform?.toLowerCase() !== 'web').length;

      // Distinct non-guest user IDs
      const uniqueIds = new Set(allLogs.map(l => l.userId).filter(id => id && id !== 'guest'));
      const hasGuest  = allLogs.some(l => !l.userId || l.userId === 'guest');
      const unique    = uniqueIds.size + (hasGuest ? 1 : 0);

      return { total, unique, web, mobile };
    } catch (e) {
      console.error('getStatsForRange error:', e);
      return { total: 0, unique: 0, web: 0, mobile: 0 };
    }
  }

  private getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) return 'Opera';
    // Edge (Chromium) must be checked BEFORE Chrome — its UA contains both "Edg/" and "Chrome"
    if (ua.indexOf('Edg/') > -1 || ua.indexOf('Edge/') > -1) return 'Edge';
    if (ua.indexOf('SamsungBrowser') > -1) return 'Samsung Internet';
    if (ua.indexOf('Chrome') > -1) return 'Chrome';
    if (ua.indexOf('Safari') > -1) return 'Safari';
    if (ua.indexOf('Firefox') > -1) return 'Firefox';
    if (ua.indexOf('MSIE') > -1 || !!(document as any).documentMode) return 'IE';
    return 'Web Browser';
  }

  /**
   * Fetch all registered users from the 'users-list' collection.
   */
  async getRegisteredUsers(): Promise<any[]> {
    const usersCol = collection(this.firestore, 'users-list');
    try {
      const snap = await getDocs(usersCol);
      return snap.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          uid: docSnap.id,
          ...data
        };
      });
    } catch (e) {
      console.error('Error fetching registered users:', e);
      return [];
    }
  }
}
